import React from 'react';
import './GenericDomainWidget.css';
import {
	DataTableProvider,
	MessageHelper,
	FeedBackType,
	DataCardList,
	PageLoader,
	Alert,
	ActionStatusHelper,
	DataList,
	WidgetFormat,
	DomainType,
	useUIFeedback,
	useDomainLayout,
	UIFeedback,
} from '@/Shared';
import { DOMAIN_CONFIGS } from '@/Shared/Config/domain-config';


interface GenericDomainWidgetProps<T> {
	domainType: DomainType;
	allowAll: boolean;
	limitRecentItems?: number,
	format: WidgetFormat,
}

/**
 * GenericDomainWidget
 *
 * Generic and reusable widget component for displaying domain data
 * in a compact format (Card or List).
 *
 * This component is configuration-driven and domain-agnostic:
 * - Domain behavior is injected via `useDomainTableConfig`.
 * - UI feedback (toast, confirm, modal) is fully centralized.
 *
 * Responsibilities:
 * - Initialize domain table configuration with UI feedback hooks.
 * - Handle loading and error states consistently.
 * - Optionally limit and sort recent items for dashboard use cases.
 * - Render data using the requested widget format (Card or List).
 *
 * This component is designed for dashboards and contextual views,
 * not full domain listing pages.
 * 
 * Author: Samano CASTRE
 * Date: January 2026
 */
const GenericDomainWidget = <T extends { title: string },>({
	domainType,
	limitRecentItems = 10,
	allowAll,
	format = 'Card'
}: GenericDomainWidgetProps<T>) => {

	const {   domainLabelPl } = DOMAIN_CONFIGS[domainType];
	
		/** UI feedback hooks shared across the layout */
		const uiFeedbackHooks = useUIFeedback();
	
		const {response, sortedTableConfig} = useDomainLayout(
			domainType,
			uiFeedbackHooks
		);


	/**
	 * Global loading and error state handling
	 */
	const { isLoading, isFailed } = ActionStatusHelper.is(response.status);

	if (isFailed) {
		return (
			<Alert
				data={{
					message: response.error || MessageHelper.LISTING.ERROR(domainLabelPl),
					type: FeedBackType.ERROR
				}}
			/>
		);
	}

	if (isLoading || !sortedTableConfig) {
		return (
			<PageLoader
				message={MessageHelper.LISTING.LOADING(domainLabelPl)}
			/>
		);
	}

	return (
		<div className={`GenericDomainWidget ${domainType}`} data-testid={domainType ?? GenericDomainWidget}>
			<UIFeedback uiFeedbackHooks={uiFeedbackHooks} />

			{/* Data rendering layer */}
			<DataTableProvider config={sortedTableConfig}>
				{format === 'Card' && <DataCardList />}
				{format === 'List' && <DataList />}
			</DataTableProvider>
		</div>
	);
};

export default GenericDomainWidget;